import { useCallback, useEffect, useState } from 'react'
import { GitBranch, Plus, Trash2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import { modelHistory, type ModelMilestone } from './modelHistory'

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export default function VersionHistoryPanel() {
  const open = useAppStore((state) => state.versionHistoryOpen)
  const setOpen = useAppStore((state) => state.setVersionHistoryOpen)
  const [rows, setRows] = useState<ModelMilestone[]>([])
  const [busy, setBusy] = useState(false)
  const activeModelId = useAppStore((state) => state.activeModelId)

  const refresh = useCallback(async () => setRows(await modelHistory.list()), [])

  // Ler a lista quando o painel abre. O `setRows` acontece depois de um
  // await, e o cancelamento evita escrever num painel que entretanto
  // fechou.
  useEffect(() => {
    if (!open) return
    let cancelado = false
    void (async () => {
      const lista = await modelHistory.list()
      if (!cancelado) setRows(lista)
    })()
    return () => {
      cancelado = true
    }
  }, [open])

  if (!open || !isFeatureEnabled('versionHistory')) return null

  const capture = async () => {
    if (!activeModelId) return
    const label = window.prompt('Nome do marco desta versão:', 'Marco de coordenação')
    if (label === null) return
    setBusy(true)
    try {
      await modelHistory.capture(activeModelId, label)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <aside className="pointer-events-auto absolute right-3 top-14 z-30 flex max-h-[75%] w-[390px] flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-sm shadow-2xl">
      <header className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <GitBranch size={16} className="text-[var(--color-accent)]" />
        <strong>Histórico de versões</strong>
        <button
          type="button"
          className="ml-auto rounded p-1 hover:bg-[var(--color-surface)]"
          onClick={() => setOpen(false)}
          title="Fechar"
        >
          <X size={15} />
        </button>
      </header>
      <div className="border-b border-[var(--color-border)] p-3">
        <button
          type="button"
          disabled={!activeModelId || busy}
          onClick={() => void capture()}
          className="flex w-full items-center justify-center gap-1 rounded bg-[var(--color-accent)] px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
        >
          <Plus size={14} />
          Guardar marco do modelo ativo
        </button>
        <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
          Cada marco guarda o IFC do modelo. Abre-o ao lado do modelo atual e usa Comparar IFC para analisar as diferenças.
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {rows.length === 0 ? (
          <p className="p-4 text-center text-xs text-[var(--color-text-muted)]">
            Ainda não há marcos guardados.
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{row.label}</div>
                    <div className="truncate text-[11px] text-[var(--color-text-muted)]">{row.sourceFilename}</div>
                    <div className="text-[10px] text-[var(--color-text-dim)]">
                      {new Date(row.createdAt).toLocaleString('pt-PT')} · {sizeLabel(row.sizeBytes)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded p-1 text-[var(--color-text-muted)] hover:bg-red-500/15 hover:text-[var(--color-error-text)]"
                    title="Apagar marco"
                    onClick={() => {
                      if (!window.confirm(`Apagar o marco “${row.label}”?`)) return
                      void modelHistory.remove(row.id).then(refresh)
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="mt-2 flex gap-1">
                  <button
                    type="button"
                    className="flex-1 rounded border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-bg)]"
                    onClick={() => void modelHistory.open(row, false)}
                  >
                    Adicionar para comparar
                  </button>
                  <button
                    type="button"
                    className="rounded border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-bg)]"
                    onClick={() => void modelHistory.open(row, true)}
                  >
                    Abrir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
